// src/views/litellm/components/HourlyChart.jsx
import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
} from "recharts";
import { fillBuckets } from "../hourly";
import { usd, compactNumber } from "../format";
import { STACK_PALETTE } from "../../cost-analyzer/AnalyzerChart.jsx";

const CHART_H = 220;

// Live's bars take the FIRST colour of the shared categorical palette — the same one a
// Trends stack opens with — so the two AI Usage modes read as one screen rather than two.
// Imported rather than copied: a palette change moves both, and they cannot drift apart.
// Live is single-series (one metric at a time), so it needs one colour, not the whole ramp.
const BAR = STACK_PALETTE[0];

// Above this many bars, value labels are suppressed (they'd overlap and clutter); the
// per-bar width check below is the hard guarantee that a label never exceeds its bar.
const MAX_LABEL_BARS = 60;

// Metric registry: cost leads (this is a financial product); tokens/events available via toggle.
const METRICS = {
  cost: { key: "cost", label: "Cost", noun: "Spend", fmt: usd },
  tokens: { key: "tokens", label: "Tokens", noun: "Tokens", fmt: compactNumber },
  events: { key: "events", label: "Events", noun: "Events", fmt: compactNumber },
};

// fmtBucket renders an ISO bucket as "HH:00" (hour) or "HH:MM" (minute) in the chosen
// timezone (UTC default; Local on toggle).
function fmtBucket(iso, tz, granularity) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const local = tz === "Local";
  const h = local ? d.getHours() : d.getUTCHours();
  const hh = String(h).padStart(2, "0");
  if (granularity === "minute") {
    const m = local ? d.getMinutes() : d.getUTCMinutes();
    return `${hh}:${String(m).padStart(2, "0")}`;
  }
  return `${hh}:00`;
}

// isoPlusBucket returns the ISO at the end of the bucket (start + one hour or one minute).
function isoPlusBucket(iso, granularity) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const stepMs = granularity === "minute" ? 60_000 : 3600_000;
  return new Date(d.getTime() + stepMs).toISOString();
}

// ChartTooltip shows the hovered bucket with all three metrics together (+ cumulative when on).
function ChartTooltip({ active, payload, tz, granularity, metricKey, cumulative }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const m = METRICS[metricKey];
  const Row = ({ label, value, strong }) => (
    <div className="flex items-center justify-between gap-6">
      <span className="text-text-3">{label}</span>
      <span className={`tabular-nums ${strong ? "font-semibold text-text" : "text-text-2"}`}>{value}</span>
    </div>
  );
  return (
    <div className="rounded-card border border-card-bd bg-card px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-semibold text-text">
        {fmtBucket(row.hour, tz, granularity)}–{fmtBucket(isoPlusBucket(row.hour, granularity), tz, granularity)} {tz}
      </div>
      <div className="space-y-0.5">
        <Row label="Cost" value={usd(row.cost)} strong={metricKey === "cost"} />
        <Row label="Tokens" value={compactNumber(row.tokens)} strong={metricKey === "tokens"} />
        <Row label="Events" value={compactNumber(row.events)} strong={metricKey === "events"} />
        {cumulative && <Row label="Cumulative" value={m.fmt(row._cum)} />}
      </div>
    </div>
  );
}

// HourlyChart plots usage per time bucket as a recharts bar chart. Which series (metricKey),
// whose clock (tz), and whether to overlay the running total (cumulative) are view props owned by
// the page — the controls that set them live in the header filter bar. Granularity (hour|minute)
// comes from the data-driving `filter`. Includes value labels atop the bars, a y-axis with
// formatted ticks, a "now" marker, an optional cumulative burn line, a multi-metric tooltip, and
// a screen-reader data table.
export default function HourlyChart({
  hourly,
  from,
  to,
  filter = {},
  metricKey = "cost",
  tz = "Local",
  cumulative = false,
}) {
  const granularity = filter.granularity === "minute" ? "minute" : "hour";
  const unitLabel = granularity === "minute" ? "minute" : "hour";

  const series = useMemo(() => fillBuckets(hourly, from, to, granularity), [hourly, from, to, granularity]);
  const m = METRICS[metricKey];

  // Attach a running cumulative for the selected metric; derive total/peak/now.
  const { data, total, peak, nowIso } = useMemo(() => {
    let cum = 0;
    let pk = null;
    const rows = series.map((b) => {
      const val = Number(b[m.key]) || 0;
      cum += val;
      if (!pk || val > (Number(pk[m.key]) || 0)) pk = b;
      return {
        hour: b.hour,
        events: Number(b.events) || 0,
        cost: Number(b.cost) || 0,
        tokens: Number(b.tokens) || 0,
        _cum: cum,
      };
    });
    return {
      data: rows,
      total: cum,
      peak: pk,
      nowIso: rows.length ? rows[rows.length - 1].hour : null,
    };
  }, [series, m.key]);

  // Only draw labels when bars are few enough to stay readable; each label is additionally
  // hidden if it would be wider than its bar (see renderBarLabel).
  const showLabels = data.length <= MAX_LABEL_BARS;

  // renderBarLabel draws a small value atop each bar, but skips zero bars and any label whose
  // estimated text width exceeds the bar width — so a label never spills past its bar.
  const renderBarLabel = (props) => {
    if (!showLabels) return null;
    const { x, y, width, value } = props;
    const v = Number(value) || 0;
    if (v <= 0 || !width) return null;
    const text = m.fmt(v);
    const estWidth = text.length * 5.2; // ~0.58em per char at 9px
    if (estWidth > width) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 3}
        textAnchor="middle"
        fontSize={9}
        fontWeight={600}
        fill="hsl(var(--text-2))"
        className="tabular-nums"
      >
        {text}
      </text>
    );
  };

  if (series.length === 0) {
    return (
      <div className="bg-card rounded-card border border-card-bd p-5 text-sm text-text-3">
        No hourly data.
      </div>
    );
  }

  const axisFmt = (v) => m.fmt(v);

  return (
    <div className="bg-card rounded-card border border-card-bd p-5">
      {/* Chart heading only — the period / granularity / timezone / metric / model / key /
          cumulative controls now live in the page header's filter bar. */}
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-3">
        {m.noun} per {unitLabel} ({tz})
      </div>

      <div style={{ height: CHART_H }} role="img" aria-label={`${m.noun} per ${unitLabel}. Total ${m.fmt(total)}.`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 14, right: cumulative ? 8 : 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke="hsl(var(--chart-grid))" strokeDasharray="3 3" />
            <XAxis
              dataKey="hour"
              tickFormatter={(v) => fmtBucket(v, tz, granularity)}
              tick={{ fontSize: 10, fill: "hsl(var(--chart-axis))" }}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--chart-grid))" }}
              interval="preserveStartEnd"
              minTickGap={28}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={axisFmt}
              tick={{ fontSize: 10, fill: "hsl(var(--chart-axis))" }}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            {cumulative && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={axisFmt}
                tick={{ fontSize: 10, fill: "hsl(var(--chart-pred))" }}
                tickLine={false}
                axisLine={false}
                width={44}
              />
            )}
            <RechartsTooltip
              cursor={{ fill: "hsl(var(--chart-grid))", fillOpacity: 0.3 }}
              content={<ChartTooltip tz={tz} granularity={granularity} metricKey={metricKey} cumulative={cumulative} />}
            />
            <Bar
              yAxisId="left"
              dataKey={m.key}
              fill={BAR}
              radius={[3, 3, 0, 0]}
              minPointSize={2}
              maxBarSize={44}
              isAnimationActive={false}
              // Faint full-height track so empty buckets read as "quiet", not missing.
              background={{ fill: "hsl(var(--chart-grid))", fillOpacity: 0.35, radius: 2 }}
              activeBar={{ fillOpacity: 0.8 }}
            >
              <LabelList dataKey={m.key} content={renderBarLabel} />
            </Bar>
            {cumulative && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="_cum"
                stroke="hsl(var(--chart-pred))"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            )}
            {nowIso && (
              <ReferenceLine
                yAxisId="left"
                x={nowIso}
                stroke="hsl(var(--chart-axis))"
                strokeDasharray="2 2"
                label={{ value: "now", position: "top", fontSize: 9, fill: "hsl(var(--chart-axis))" }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Running total + peak. The "last updated" stamp lives in the page header now (beside
          Refresh), so it isn't repeated here. */}
      <div className="mt-2 text-[11px] text-text-3 tabular-nums">
        Total: <span className="font-semibold text-text-2">{m.fmt(total)}</span>
        {peak && (Number(peak[m.key]) || 0) > 0 && (
          <> · peak {fmtBucket(peak.hour, tz, granularity)} ({m.fmt(peak[m.key])})</>
        )}
      </div>

      {/* Screen-reader equivalent of the bars. */}
      <table className="sr-only">
        <caption>{m.noun} per {unitLabel} ({tz})</caption>
        <thead>
          <tr>
            <th>{granularity === "minute" ? "Minute" : "Hour"}</th>
            <th>Cost</th>
            <th>Tokens</th>
            <th>Events</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.hour} data-testid="hour-row">
              <td>{fmtBucket(d.hour, tz, granularity)}</td>
              <td>{usd(d.cost)}</td>
              <td>{compactNumber(d.tokens)}</td>
              <td>{compactNumber(d.events)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
